import { Box, Heading, HStack, Pressable, Text, useToken, VStack } from 'native-base';
import React, { useMemo } from 'react';

import HTML from 'app/components/HTML';
import {
  CardActionArrow,
  ChevronRight,
  ExclamationCircle,
  hasProductSpotLogo,
} from 'app/components/icons';
import useDestinations from 'app/hooks/useDestinations/useDestinations';
import useGuest from 'app/hooks/useGuest';
import useProduct from 'app/hooks/useProduct';
import { useI18n } from 'app/providers/I18nProvider';
import DestinationCard from 'app/screens/DestinationsScreen/DestinationCard/DestinationCard';
import { Entitlement, TicketClass } from 'app/services/GuestCenterService.types';
import { tID } from 'app/services/TestHelper';
import { areAllPassesExpired, isEntitlementSelectable } from 'app/utils/time';

const INITIAL_PAST_TRIPS_COUNT = 2;
const PAST_TRIP_GRAY_3 = '#646467';

export type PurchasesTripsListProps = {
  email?: string | null;
  fadePastTrips?: boolean;
  onViewOrderDetails: (entitlementKey: string) => void;
  onViewAllPastTrips?: () => void;
  showAllPastTrips?: boolean;
  showMyTrips?: boolean;
  showPastTripsHeading?: boolean;
};

function PurchasesTripCard({
  active,
  entitlement,
  fadePastTrip = true,
  isPastTrip,
  onViewOrderDetails,
}: {
  active: boolean;
  entitlement: Entitlement;
  fadePastTrip?: boolean;
  isPastTrip: boolean;
  onViewOrderDetails: () => void;
}) {
  const { t } = useI18n();
  const activeSpotColor = useToken('colors', 'brand.primary');
  const { data: destinations = [] } = useDestinations();
  const productContentKey =
    entitlement.productContentKey ?? entitlement.productLineup?.contentKey ?? '';
  const product = destinations.find((d) => d.key === productContentKey);
  const prefersLocalSpotLogo = hasProductSpotLogo(productContentKey);
  const { productContent } = useProduct({
    enabled: active && !prefersLocalSpotLogo && !!productContentKey,
    productContentKey,
  });
  let spotColor: string;
  if (active) {
    spotColor = activeSpotColor;
  } else {
    spotColor = 'black';
  }
  const faded = isPastTrip && fadePastTrip;
  const orderTitleColor = faded ? PAST_TRIP_GRAY_3 : 'black';
  const orderDetailsColor = faded ? 'gray.400' : 'coolGray.500';
  const ticketClassSummary = t('app_ticket_class_summary', {
    adult: entitlement.quantity[TicketClass.Adult],
    child: entitlement.quantity[TicketClass.Child],
  });
  const ticketClassParts = ticketClassSummary.match(/^(.*?)(\s[-–]\s)(.*)$/);
  const adultCount = ticketClassParts?.[1] ?? ticketClassSummary;
  const ticketClassSeparator = ticketClassParts?.[2] ?? '';
  const childCount = ticketClassParts?.[3] ?? '';
  const productIdentifier = product?.slug || productContentKey || 'unknown-product';
  const tripCardTestID = `TripCard-${productIdentifier}-${entitlement.entitlementKey}`;

  return (
    <Pressable testID={tID(tripCardTestID)} onPress={onViewOrderDetails}>
      <Box bg="white" borderColor="coolGray.200" borderWidth="1" p="2" rounded="md" shadow="1">
        <HStack alignItems="center" space="3">
          <Box h="72px" overflow="hidden" rounded="sm" w="72px">
            {product ? (
              <DestinationCard
                muted={faded}
                preferLocalSpotLogo={prefersLocalSpotLogo}
                product={product}
                rounded="sm"
                showLabel={false}
                spotColor={spotColor}
                spotSize={20}
                spotUriOverride={
                  active && !prefersLocalSpotLogo ? productContent?.logoSpotOutline?.url : undefined
                }
                onPress={onViewOrderDetails}
              />
            ) : (
              <Box
                alignItems="center"
                bg="gray.100"
                h="72px"
                justifyContent="center"
                rounded="10px"
                w="72px"
              >
                <ExclamationCircle color="red" size={24} />
              </Box>
            )}
          </Box>

          <VStack flex="1" space="1">
            <Heading
              color={orderTitleColor}
              fontSize="sm"
              numberOfLines={1}
              testID={tID(`${tripCardTestID}-ProductName`)}
            >
              {entitlement.name}
            </Heading>
            <VStack space="0">
              {!!entitlement.travelParty?.orderNumber && (
                <Text
                  color={orderDetailsColor}
                  fontSize="sm"
                  lineHeight="sm"
                  testID={tID(`${tripCardTestID}-OrderNumber`)}
                >
                  {t('app_order_num_title')} {entitlement.travelParty.orderNumber}
                </Text>
              )}
              <Text color={orderDetailsColor} fontSize="sm" lineHeight="sm">
                <Text testID={tID(`${tripCardTestID}-AdultCount`)}>{adultCount}</Text>
                {ticketClassSeparator}
                <Text testID={tID(`${tripCardTestID}-ChildCount`)}>{childCount}</Text>
              </Text>
            </VStack>
          </VStack>

          <Pressable
            opacity={faded ? 0.4 : 1}
            p="1"
            testID={tID(`TripCardAction-${entitlement.entitlementKey}`)}
            onPress={onViewOrderDetails}
          >
            <CardActionArrow size={34} />
          </Pressable>
        </HStack>
      </Box>
    </Pressable>
  );
}

export default function PurchasesTripsList({
  email,
  fadePastTrips = true,
  onViewOrderDetails,
  onViewAllPastTrips,
  showAllPastTrips = false,
  showMyTrips = true,
  showPastTripsHeading = true,
}: PurchasesTripsListProps) {
  const { t } = useI18n();
  const { activeEntitlement, allEntitlements } = useGuest();

  const { myTrips, pastTrips } = useMemo(() => {
    return allEntitlements.reduce<{
      myTrips: Entitlement[];
      pastTrips: Entitlement[];
    }>(
      (acc, entitlement) => {
        if (areAllPassesExpired(entitlement)) {
          acc.pastTrips.push(entitlement);
        } else {
          acc.myTrips.push(entitlement);
        }
        return acc;
      },
      { myTrips: [], pastTrips: [] }
    );
  }, [allEntitlements]);

  const visiblePastTrips = showAllPastTrips
    ? pastTrips
    : pastTrips.slice(0, INITIAL_PAST_TRIPS_COUNT);
  const shouldShowPastTripsToggle = pastTrips.length > INITIAL_PAST_TRIPS_COUNT;
  const shouldShowMyTripsEmptyState = showMyTrips && myTrips.length === 0 && pastTrips.length > 0;

  const handleViewOrderDetails = (entitlement: Entitlement, isPastTrip: boolean) => {
    if (isPastTrip || isEntitlementSelectable(entitlement)) {
      onViewOrderDetails(entitlement.entitlementKey);
    }
  };

  return (
    <VStack space="6" testID={tID('PurchasesTripsList')}>
      {showMyTrips && !!myTrips.length && (
        <VStack space="3">
          <Heading fontSize="lg">{t<string>('app_purchases_my_trips')}</Heading>
          <VStack space="3">
            {myTrips.map((entitlement) => (
              <PurchasesTripCard
                key={entitlement.entitlementKey}
                active={activeEntitlement?.entitlementKey === entitlement.entitlementKey}
                entitlement={entitlement}
                isPastTrip={false}
                onViewOrderDetails={() => handleViewOrderDetails(entitlement, false)}
              />
            ))}
          </VStack>
        </VStack>
      )}

      {shouldShowMyTripsEmptyState && (
        <VStack space="3">
          <Heading fontSize="lg">{t<string>('app_purchases_my_trips')}</Heading>
          <VStack space="3">
            <HTML html={t<string>('app_auth_tickets_not_found', { email: email ?? '' })} />
            <HTML html={t<string>('app_purchases_my_trips_empty_with_past_trips_help')} />
          </VStack>
        </VStack>
      )}

      {!!pastTrips.length && (
        <VStack space="3">
          {showPastTripsHeading && (
            <Heading fontSize="lg">{t<string>('app_purchases_past_trips')}</Heading>
          )}
          <VStack space="3">
            {visiblePastTrips.map((entitlement) => (
              <PurchasesTripCard
                key={entitlement.entitlementKey}
                active={false}
                entitlement={entitlement}
                fadePastTrip={fadePastTrips}
                isPastTrip
                onViewOrderDetails={() => handleViewOrderDetails(entitlement, true)}
              />
            ))}
          </VStack>

          {shouldShowPastTripsToggle && !showAllPastTrips && (
            <Pressable
              alignSelf="flex-start"
              testID={tID('ViewAllPastTripsButton')}
              onPress={onViewAllPastTrips}
            >
              <HStack alignItems="center" space="1">
                <Text bold color="brand.secondary" underline>
                  {t<string>('app_purchases_view_all_past_trips')}
                </Text>
                <ChevronRight color={PAST_TRIP_GRAY_3} size={10} />
              </HStack>
            </Pressable>
          )}
        </VStack>
      )}
    </VStack>
  );
}
